import type { HTMLAttributes } from 'react';
import type { SalesType } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import { UNKNOWN_TRADE, tradeLabel, tradeTint, type Trade } from '../../lib/trade.js';

interface TradePillProps extends HTMLAttributes<HTMLSpanElement> {
  types: ReadonlyArray<SalesType>;
  trade: Trade;
  size?: 'sm' | 'md';
  dot?: boolean;
  /** Render nothing for 'unknown' instead of a neutral "Job" pill. */
  hideUnknown?: boolean;
}

/**
 * A job's sales type as a pill, coloured from the type's own hex (Admin →
 * Sales types). Replaces `<Pill variant={tradeToPillVariant(trade)}>` now
 * that types aren't a fixed set with palette entries.
 */
export function TradePill({
  types,
  trade,
  size = 'sm',
  dot = false,
  hideUnknown = false,
  className,
  style,
  ...rest
}: TradePillProps) {
  if (hideUnknown && trade === UNKNOWN_TRADE) return null;
  const tint = tradeTint(types, trade);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-medium whitespace-nowrap',
        size === 'sm' ? 'text-[10px] px-2 py-0.5 leading-4' : 'text-xs px-2.5 py-1',
        className,
      )}
      style={{ backgroundColor: tint.bg, color: tint.fg, ...style }}
      {...rest}
    >
      {dot && (
        <span
          aria-hidden
          className="inline-block h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: tint.solid }}
        />
      )}
      {tradeLabel(types, trade)}
    </span>
  );
}
